import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import { updateHPOTermLabels } from '@/features/terminology/hpo/HPOTermLabelUpdater'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { mergeReason, setCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import { NavigationSection, OrderStatusDisplay } from '@/types/NavigationTypes'
import { CategorizedHPOOption, OrderFormInfoData, RequisitionFormData } from '@/types/RequisitionFormTypes'
import { legacyTrfPayloadToCanonical } from '@/ordering/canonicalOrder'
import { getOrderStatusByPatient, OrderStatus, updateOrder } from '@/utils/DatabaseOperations'
import { isOrderReadOnly } from '@/utils/OrderEditability'
import { Button } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import { ExclamationCircle } from 'flowbite-react-icons/outline'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Layout from '../Layout'
import HPOTerms from './HPOTerms'
import Navigation from './Navigation'
import OrderReport from './OrderReport'
import OrderSummary from './OrderSummary'
import RequisitionFormView from './RequisitionFormView'

/**
 * OrderPlacedConfirmation Component
 *
 * Displays a confirmation screen after an order has been successfully placed.
 * Shows order details, status timeline, and the generated requisition form PDF.
 * Users can click on "Results Pending" status to navigate to the results screen.
 *
 * Features:
 * - Generates and displays the requisition form PDF inline
 * - Shows order details (ID, patient info, test type, date)
 * - Interactive status timeline with clickable "Results Pending" item
 * - Matches the design system and layout of the order form
 */
// Helper to map backend order status to UI display status
const mapOrderStatusToDisplay = (backendStatus?: string): OrderStatusDisplay => {
  if (!backendStatus) return OrderStatusDisplay.ORDER_PLACED

  switch (backendStatus) {
    case OrderStatus.PENDING:
      return OrderStatusDisplay.ORDER_PLACED
    case OrderStatus.SUBMITTED:
      return OrderStatusDisplay.ORDER_SUBMITTED
    case OrderStatus.ACCESSIONED:
      return OrderStatusDisplay.SAMPLE_RECEIVED
    case OrderStatus.COMPLETE:
      return OrderStatusDisplay.RESULT_READY
    default:
      return OrderStatusDisplay.ORDER_PLACED
  }
}

const OrderPlacedConfirmation: React.FC = () => {
  const location = useLocation()
  const locationState = location.state as
    | {
        state?: string
        orderId?: string
        orderData?: OrderFormInfoData
        reportId?: string
        formData?: RequisitionFormData
      }
    | string
    | null
    | undefined

  const orderId = typeof locationState === 'object' && locationState !== null ? locationState.orderId : undefined
  const orderData = typeof locationState === 'object' && locationState !== null ? locationState.orderData : undefined
  const passedFormData =
    typeof locationState === 'object' && locationState !== null ? locationState.formData : undefined

  const dispatch = useAppDispatch()
  const [activeSection, setActiveSection] = useState<NavigationSection>('order-summary')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const [hpoTermsResetKey, setHpoTermsResetKey] = useState(0)
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [orderStatus, setOrderStatus] = useState<OrderStatusDisplay>(OrderStatusDisplay.ORDER_PLACED)

  // Track if we've already auto-navigated to lab report (to prevent repeated forced navigation)
  const hasAutoNavigatedToLabReport = useRef(false)

  // Ref to track if HPO labels have been updated
  const hpoLabelsUpdatedRef = useRef(false)

  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)
  const [loadedFormData, setLoadedFormData] = useState<RequisitionFormData | null>(null)
  const formData: RequisitionFormData | null = passedFormData || loadedFormData || null

  // Load canonical from order's json_payload if not yet hydrated
  useEffect(() => {
    if (passedFormData || storedCanonical) return

    if (orderId && orderData?.patient?.id) {
      const loadCanonicalFromOrder = async () => {
        try {
          const { getOrderById } = await import('@/utils/DatabaseOperations')
          const caller = orderData?.orderingProvider?.name
            ? `${orderData.orderingProvider.name.firstName || ''} ${orderData.orderingProvider.name.lastName || ''}`.trim()
            : 'spa'
          const order = await getOrderById(orderId, caller)
          if (order?.json_payload?.requisitionFormData) {
            setLoadedFormData(order.json_payload.requisitionFormData as RequisitionFormData)
            dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
              orderFormInfo: order.json_payload.orderFormInfo,
              requisitionFormData: order.json_payload.requisitionFormData,
            })))
          }
        } catch (error) {
          Sentry.captureException(error, { tags: { operation: 'order.load_canonical' } })
        }
      }
      loadCanonicalFromOrder()
    }
  }, [orderId, orderData?.patient?.id, passedFormData, storedCanonical, dispatch])

  // Compute caller from ordering provider name
  const caller = React.useMemo(() => {
    if (orderData?.orderingProvider?.name) {
      const firstName = orderData.orderingProvider.name.firstName || ''
      const lastName = orderData.orderingProvider.name.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || 'spa'
    }
    return 'spa'
  }, [orderData?.orderingProvider?.name])

  // Fetch order status from API
  useEffect(() => {
    const fetchOrderStatus = async () => {
      if (!orderData?.patient?.id) {
        return
      }

      try {
        const statusResponse = await getOrderStatusByPatient(orderData.patient.id, caller)
        if (statusResponse) {
          const displayStatus = mapOrderStatusToDisplay(statusResponse.status)
          setOrderStatus(displayStatus)

          // Auto-navigate to lab report only once when results first become ready
          // This prevents repeated forced navigation during polling that would
          // bypass unsaved changes checks and disrupt user workflow
          if (displayStatus === OrderStatusDisplay.RESULT_READY && !hasAutoNavigatedToLabReport.current) {
            hasAutoNavigatedToLabReport.current = true
            setActiveSection('lab-report')
          }
        }
      } catch (error) {
        console.error('Error fetching order status:', error)
        // Default to ORDER_PLACED on error
        setOrderStatus(OrderStatusDisplay.ORDER_PLACED)
      }
    }

    fetchOrderStatus()

    // Poll for status updates every 10 minutes
    const intervalId = setInterval(fetchOrderStatus, 600000)
    return () => clearInterval(intervalId)
  }, [orderData?.patient?.id, caller])

  // Update HPO term labels on form load using getHPOTermById
  useEffect(() => {
    const updateHPOLabels = async () => {
      if (hpoLabelsUpdatedRef.current) return

      const existingTerms = storedCanonical?.reason?.clinicalDiagnosis
      if (!existingTerms || existingTerms.length === 0) return

      try {
        Sentry.startSpan(
          { op: 'hpo.update_labels', name: 'Update HPO Term Labels on Form Load' },
          async (span) => {
            const getTermById = (id: string) => hpoAPIClient.getHPOTermById(id)
            const updatedTerms = await updateHPOTermLabels(existingTerms, getTermById)
            const labelsChanged = updatedTerms.some(
              (updatedTerm, index) => updatedTerm.label !== existingTerms[index]?.label
            )
            if (labelsChanged) {
              dispatch(mergeReason({ clinicalDiagnosis: updatedTerms }))
            }
            span.setAttribute('hpo.reason_term_count', existingTerms.length)
            span.setAttribute('hpo.labels_changed', labelsChanged)
            hpoLabelsUpdatedRef.current = true
          }
        )
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'update.hpo.labels' } })
      }
    }

    updateHPOLabels()
  }, [storedCanonical?.reason?.clinicalDiagnosis, dispatch])

  // Helper to check and handle navigation with unsaved changes
  const handleNavigationWithCheck = (targetSection: NavigationSection) => {
    // Only show modal if we're leaving HPO Terms section and there are unsaved changes
    if (activeSection === 'hpo-terms' && targetSection !== 'hpo-terms' && hasUnsavedChanges) {
      setPendingNavigation(() => () => setActiveSection(targetSection))
      setShowUnsavedChangesModal(true)
    } else {
      setActiveSection(targetSection)
    }
  }

  const handleUpdateHPO = async (hpoTerms: CategorizedHPOOption[]) => {
    if (!orderId || !orderData) {
      setUpdateError('Missing order ID or order data. Cannot update order.')
      return
    }

    setIsUpdatingOrder(true)
    setUpdateError(null)

    try {
      // Update the order in the database first (pass {} as legacy TRF shell; canonical carries the data)
      await updateOrder(orderId, orderData, {}, OrderStatus.SUBMITTED, caller, {
        canonicalOrder: storedCanonical,
      })

      // Update canonical store after successful database update
      dispatch(mergeReason({ clinicalDiagnosis: hpoTerms, userInteracted: true }))

      // Clear unsaved changes flag since update was successful
      setHasUnsavedChanges(false)

      // Navigate back to order summary
      setActiveSection('order-summary')
    } catch (error) {
      console.error('Error updating order with HPO terms:', error)
      setUpdateError(error instanceof Error ? error.message : 'Failed to update order')
      // Don't update Redux if database update fails - this keeps state consistent
      // and allows user to retry the update
    } finally {
      setIsUpdatingOrder(false)
    }
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        setActiveSection('order-summary')
      })
      setShowUnsavedChangesModal(true)
    } else {
      setActiveSection('order-summary')
    }
  }

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        setActiveSection('order-summary')
      })
      setShowUnsavedChangesModal(true)
    } else {
      setActiveSection('order-summary')
    }
  }

  const handleKeepEditing = () => {
    setShowUnsavedChangesModal(false)
    setPendingNavigation(null)
  }

  const handleDiscardChanges = () => {
    // Reset HPO terms by forcing a remount of the component
    setHpoTermsResetKey((prev) => prev + 1)
    setHasUnsavedChanges(false)
    setShowUnsavedChangesModal(false)
    // Execute pending navigation if any
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  return (
    <Layout allowScrolling={false}>
      <div className="h-full bg-background-secondary-medium dark:bg-gray-800">
        <main className="h-full w-full">
          <div className="flex h-full">
            {/* Left Column - Navigation Sidebar */}
            <Navigation
              activeSection={activeSection}
              onNavigationClick={handleNavigationWithCheck}
              hasUnsavedChanges={hasUnsavedChanges}
              orderStatus={orderStatus}
            />

            {/* Right Section - Dynamic Content Based on Navigation */}
            <div className="flex flex-1 flex-col">
              <div className="m-0 flex h-14 shrink-0 items-center gap-2 border border-border-base bg-background-secondary-medium px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex w-full items-center">
                  <div>
                    <h2 className="text-base leading-6 font-normal text-text-heading dark:text-white">
                      {activeSection === 'order-summary' && 'Order Summary'}
                      {activeSection === 'hpo-terms' && 'HPO Terms'}
                      {activeSection === 'requisition-form' && 'Requisition Form'}
                      {activeSection === 'lab-report' && 'Laboratory Report'}
                    </h2>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-background-secondary-medium p-6 dark:bg-gray-800">
                {/* Order Summary Section */}
                {activeSection === 'order-summary' && (
                  <OrderSummary orderId={orderId} orderData={orderData} formData={formData ?? undefined} />
                )}

                {/* HPO Terms Section */}
                {activeSection === 'hpo-terms' && (
                  <>
                    {updateError && (
                      <div className="mb-4 rounded-base border border-border-error-subtle bg-error-50 p-4 dark:border-error-700 dark:bg-gray-900">
                        <div className="flex items-center gap-2">
                          <ExclamationCircle className="h-5 w-5 text-text-fg-error dark:text-error-100" />
                          <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">{updateError}</p>
                        </div>
                      </div>
                    )}
                    <HPOTerms
                      key={hpoTermsResetKey}
                      formData={formData ?? undefined}
                      onUpdate={handleUpdateHPO}
                      onCancel={handleCancel}
                      onBack={handleBack}
                      onUnsavedChangesCheck={setHasUnsavedChanges}
                      isUpdating={isUpdatingOrder}
                      readOnly={isOrderReadOnly(orderStatus)}
                    />
                  </>
                )}

                {/* Requisition Form Section */}
                {activeSection === 'requisition-form' && (
                  <RequisitionFormView
                    orderData={orderData}
                    canonical={storedCanonical}
                    formData={formData}
                    orderId={orderId}
                    trfFetchCaller={caller}
                  />
                )}

                {/* Lab Report Section */}
                {activeSection === 'lab-report' && <OrderReport orderId={orderId} orderData={orderData} />}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedChangesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="w-full max-w-md rounded-base border border-border-base bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-text-heading dark:text-white">Unsaved Changes</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">
                You have unsaved changes in this section. If you leave now, your changes will be lost.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-border-base px-6 py-4 dark:border-gray-700">
              <button
                onClick={handleKeepEditing}
                className="rounded-full border border-border-base-medium bg-white px-4 py-2 text-sm font-medium text-text-body hover:bg-background-secondary-medium focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                Keep Editing
              </button>
              <Button
                variant="danger"
                size="md"
                onClick={handleDiscardChanges}
                className="focus-visible:ring-error-500"
              >
                Discard Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default OrderPlacedConfirmation
